"use client";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconButton } from "./ui/icon-button";

export function Modal({
  open, onClose, title, sub, children, footer, size = "md",
}: {
  open: boolean; onClose: () => void; title: string; sub?: string;
  children: React.ReactNode; footer?: React.ReactNode;
  size?: "md" | "lg";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Scroll state of the body. A long form (the tenant modal) scrolls under the
  // header, and without these the first visible field is sliced clean in half
  // against the header border — it reads as a rendering bug rather than as
  // "there is more above". `scrolledTop` drives a fade + header shadow so the
  // content visibly runs *under* the header instead of being cut off by it.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scrolledTop, setScrolledTop] = useState(false);
  const [scrolledBottom, setScrolledBottom] = useState(false);

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setScrolledTop(el.scrollTop > 1);
    setScrolledBottom(el.scrollHeight - el.clientHeight - el.scrollTop > 1);
  }, []);

  // Layout effect so the first paint after open already carries the right
  // state — otherwise a scrollable body flashes without its bottom fade.
  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // Content grows when a validation error appears at the bottom of the form.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [open, measure]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div
        className={cn(
          // `rounded-xl` explicitly: `.card` dropped to 8px when cards became
          // object-only, but a dialog floats above the page and keeps the
          // softer 12px edge it has always had.
          "w-full card rounded-xl p-0 overflow-hidden flex flex-col max-h-[90vh]",
          size === "lg" ? "max-w-2xl" : "max-w-lg",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className={cn(
            "relative z-10 flex items-start justify-between px-5 py-4 border-b shrink-0 bg-bg-raised transition-shadow",
            scrolledTop
              ? "border-line shadow-[0_10px_18px_-14px_rgba(0,0,0,0.95)]"
              : "border-line-subtle",
          )}
        >
          <div>
            <h2 className="text-[14.5px] font-semibold text-fg-bright tracking-tight">{title}</h2>
            {sub && <p className="text-[12px] text-fg-dim mt-1 leading-relaxed">{sub}</p>}
          </div>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </header>
        {/* flex-col, not h-full on the child: this wrapper is itself a flex
            item with an auto computed height, so a percentage height on the
            body would resolve to its content and spill over the footer. */}
        <div className="relative flex-1 min-h-0 flex flex-col">
          <div
            ref={bodyRef}
            onScroll={measure}
            className="subtle-scroll flex-1 min-h-0 px-5 py-4 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]"
          >
            {children}
          </div>
          {/* Fades: content dissolves into the chrome at both ends instead of
              ending on a hard cut. pointer-events-none keeps fields clickable
              underneath. */}
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-bg-raised to-bg-raised/0 transition-opacity",
              scrolledTop ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-bg-raised to-bg-raised/0 transition-opacity",
              scrolledBottom ? "opacity-100" : "opacity-0",
            )}
          />
        </div>
        {footer && <footer className="px-5 py-3 border-t border-line-subtle bg-bg-base/60 flex items-center justify-end gap-2 shrink-0">{footer}</footer>}
      </div>
    </div>
  );
}
